import type { DocumentData } from "firebase/firestore/lite";
import type { FormikProps } from "formik";
import type React from "react";
import { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type getPhaseOfDateRange, useAuthContext } from "senf-shared";
import type { Project, User } from "senf-shared/types";
import LanguageButton from "../../../components/molecules/languageSettings/LanguageButton";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import type { IconName } from "../../core/icon/names";
import ProgressBar from "../../core/progressBar/ProgressBar";
import Toast from "../../core/toast/Toast";
import AccessibilitySettings from "../accessibilitySettings/AccessibilitySettings";
import SurveyWrapper from "../survey/SurveyWrapper";
import { useNavigationIndex, usePrepareSurveyPage } from "./hooks";
import { useNextDisabled } from "./hooks/useNextDisabled";
import type { Result } from "./legacy/survey.types";
import type { Page, PageType, Survey } from "./survey.config";
import { validMapInput } from "./utils";

interface ViewSurveyProps {
  isMobile: boolean;
  survey: Survey;
  formik: FormikProps<Result["values"]>;
  loginFormik: FormikProps<{
    email: string;
    password: string;
  }>;
  demographicsFormik: FormikProps<User>;
  project: Project;
  handleClose?: () => void;
  submitting: boolean;
  setSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  phase: ReturnType<typeof getPhaseOfDateRange>;
  handleProcessNext: (user?: DocumentData) => Promise<boolean>;
  index: number;
  setIndex: React.Dispatch<React.SetStateAction<number>>;
  variant?: "fullscreen" | "default";
  surveyMode?: "test" | "preview" | null;
  surveyLoginFormShown?: boolean;
  setSurveyLoginFormShown?: React.Dispatch<React.SetStateAction<boolean>>;
  user?: DocumentData;
  preview?: "mobile" | "tablet" | "desktop";
  className?: string;
}
interface NavigationButtonProps {
  disabled: boolean;
  disabledNotice: string;
  show: boolean;
  onClick: () => void;
  loading?: boolean;
  trailingIcon?: IconName;
  text?: string;
  tooltipLabel?: string;
  ariaLabel?: string;
}
interface NavigationButtonsProps extends React.HTMLAttributes<HTMLDivElement> {
  prev: NavigationButtonProps;
  next: NavigationButtonProps;
}
const NavigationButtons = ({
  prev,
  next,
  className,
  extraButton,
}: NavigationButtonsProps) => {
  const { t } = useTranslation();
  const { show: showPrev, ariaLabel: ariaLabelPrev, ...prevProps } = prev;
  const { show: showNext, ariaLabel: ariaLabelNext, ...nextProps } = next;

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: we are in a form and using fieldset here could be confising
      role="group"
      className={cn("flex items-center gap-2 p-5 justify-end", className)}
      id="survey-navigation-container"
    >
      {extraButton}
      {/* {showNext !== false && !nextProps.disabled && (
        <Button
          variant="shapeless"
          leadingIcon="enter"
          text={t("util:pressKey", { key: t("global:enter") })}
          size="sm"
          className="text-text-subdued pointer-events-none"
          tabIndex={-1}
        />
      )} */}
      {showPrev !== false && (
        <Button
          size="lg"
          leadingIcon="arrowhead-left-md"
          aria-label={ariaLabelPrev}
          {...prevProps}
        />
      )}
      {showNext !== false && (
        <Button
          variant="primary"
          size="lg"
          id="take-survey-continue-button"
          aria-label={ariaLabelNext}
          {...nextProps}
        />
      )}
    </div>
  );
};

interface CloseSurveyProps extends ViewSurveyProps {
  fullscreenMap: boolean;
}

const CloseSurvey = ({ handleClose, preview }: CloseSurveyProps) => {
  const { t } = useTranslation();
  // useOnKeyDown((e) => {
  //   if (e.code === "Escape") {
  //     handleClose();
  //   }
  // });
  return (
    <Button
      leadingIcon="x-md"
      tooltipLabel={t("util:close", { val: t("global:survey") })}
      onClick={handleClose}
      dataCy="take-survey-close-button"
      classNameWrapper="fixed top-0 z-50 m-5 end-0"
    />
  );
};

interface SurveyFooterProps extends ViewSurveyProps {
  currentPage: Page;
  currentPageRole: string;
  setFullscreenMap: React.Dispatch<React.SetStateAction<boolean>>;
  featureId?: string;
  setFeatureId: React.Dispatch<React.SetStateAction<string | undefined>>;
  openDescription: boolean;
  setOpenDescription: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile: boolean;
  className?: string;
  mainRef: React.RefObject<HTMLElement>;
}

const SurveyFooter = ({
  setSurveyLoginFormShown,
  surveyLoginFormShown,
  demographicsFormik,
  loginFormik,
  currentPageRole,
  survey,
  phase,
  formik,
  index,
  setFullscreenMap,
  submitting,
  setSubmitting,
  featureId,
  setFeatureId,
  openDescription,
  setOpenDescription,
  setIndex,
  handleProcessNext,
  isMobile,
  project,
  preview,
  mainRef,
}: SurveyFooterProps) => {
  const { t } = useTranslation();
  const { signIn, createUser } = useAuthContext();
  const { pages, formattedEndDate, formattedStartDate } = survey;
  const { nextIndex, previousIndex, isLastPage } = useNavigationIndex({
    pages,
    index,
    formik,
  });

  const next = useNextDisabled({
    pages,
    index,
    formik,
    demographicsFormik,
    mapInputState: {
      featureId,
      openDescription,
    },
  });

  const handleScrollUp = () => {
    const element = document.querySelectorAll("#surveyWrapper");
    for (const e of element) {
      e.scrollTo({
        top: 0,
        left: 0,
      });
    }
  };

  // Put these near your component
  const FOCUSABLE_SELECTOR =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [contenteditable="true"], [tabindex]';

  const isVisible = (el: HTMLElement) =>
    !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length) &&
    getComputedStyle(el).visibility !== "hidden";

  const getFocusable = (root?: HTMLElement | null) =>
    Array.from(
      root?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
    ).filter(isVisible);

  useEffect(() => {
    console.log("currentPageRole", currentPageRole);
    if (currentPageRole === "intro") {
      console.log("intro");
      // Add a small delay to ensure the DOM is ready
      setTimeout(() => {
        // Find the start button with specific ID
        const startButton = document.getElementById(
          "take-survey-continue-button",
        ) as HTMLElement;

        if (startButton) {
          console.log("startButton", startButton);
          // Find the element that comes before the start button in DOM order
          let prevElement = startButton.previousElementSibling as HTMLElement;

          // Keep going back until we find a focusable element or reach the beginning
          while (prevElement && !prevElement.matches(FOCUSABLE_SELECTOR)) {
            prevElement = prevElement.previousElementSibling as HTMLElement;
          }

          // If we found a previous focusable element, focus on it then blur
          if (prevElement) {
            console.log("Found previous element:", prevElement);
            prevElement.focus({ preventScroll: true });
            setTimeout(() => prevElement?.blur(), 0);
          } else {
            // If no previous focusable element, try to find the last focusable element before the start button
            const allFocusable = Array.from(
              document.querySelectorAll(FOCUSABLE_SELECTOR),
            ) as HTMLElement[];
            const startButtonIndex = allFocusable.indexOf(startButton);

            if (startButtonIndex > 0) {
              const elementBeforeStart = allFocusable[startButtonIndex - 1];
              console.log(
                "Found element before start in focusable list:",
                elementBeforeStart,
              );
              elementBeforeStart.focus({ preventScroll: true });
              setTimeout(() => elementBeforeStart?.blur(), 0);
            } else {
              // If no previous focusable element, focus on the main container
              mainRef.current?.focus({ preventScroll: true });
              setTimeout(() => mainRef.current?.blur(), 0);
            }
          }
        } else {
          // Fallback to main container if start button not found
          mainRef.current?.focus({ preventScroll: true });
          setTimeout(() => mainRef.current?.blur(), 0);
        }
      }, 1000);
    }
  }, [currentPageRole]);

  const handleFocusManagement = () => {
    // Focus on the first focusable element within the survey content for better accessibility
    setTimeout(() => {
      const all = getFocusable(mainRef.current);
      if (!all.length) return;

      const first = all[0];

      // Find the element that comes before the first focusable element in DOM order
      let prevElement = first.previousElementSibling as HTMLElement;

      // Keep going back until we find a focusable element or reach the beginning
      while (prevElement && !prevElement.matches(FOCUSABLE_SELECTOR)) {
        prevElement = prevElement.previousElementSibling as HTMLElement;
      }

      // If we found a previous focusable element, focus on it then blur
      if (prevElement) {
        prevElement.focus({ preventScroll: true });
        setTimeout(() => prevElement?.blur(), 0);
      } else {
        // If no previous focusable element, focus on the main container
        mainRef.current?.focus({ preventScroll: true });
        setTimeout(() => mainRef.current?.blur(), 0);
      }
    }, 100);
  };

  const handlePrev = () => {
    // console.log("handlePrev");
    const currentPageId = pages[index]?.uid;
    const currentPage = {
      ...(formik.values[currentPageId] || {}),
    };
    delete currentPage.viewed;

    formik.setFieldValue(currentPageId, currentPage);

    setIndex((e) => (previousIndex !== -1 ? previousIndex : e - 1));
    handleScrollUp();
    setFullscreenMap(false);
    setFeatureId(undefined);
    setOpenDescription(false);

    handleFocusManagement();
  };

  const handleNext = () => {
    // console.log("handleNext");
    handleProcessNext()
      .then((success: boolean) => {
        if (success) {
          setIndex(nextIndex);
          handleScrollUp();
          setFullscreenMap(false);
          // Focus on the first focusable element within the survey content for better accessibility
          handleFocusManagement();
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };
  const inactiveText = {
    before: `${t("survey:surveyNotOpenYet")} ${formattedStartDate}`,
    after: `${t("survey:surveyClosed")} ${formattedEndDate}`,
    notYetScheduled: t("survey:surveyNotYetScheduled"),
  }[phase];

  let activeText = {
    intro: t("util:start", { val: t("global:survey") }),
    surveyPage: t("global:next"),
    demographics: t("global:next"),
  }[currentPageRole];

  let onClickPrev = handlePrev;
  let onClickNext = handleNext;
  let disabled = !!(next.disabled || submitting || phase !== "while");
  let disabledNotice = next.message.current;
  const disabledPrev = false;
  const disabledPrevNotice = "";
  let trailingIcon: IconName = "arrowhead-right-md";

  if (currentPageRole === "intro") {
    if (surveyLoginFormShown) {
      const providerMap = {
        "google.com": {
          text: t("auth:authOptions.googleLogin"),
          icon: "logo/Google",
          provider: "google",
        },
        "facebook.com": {
          text: t("auth:authOptions.facebookLogin"),
          icon: "logo/Facebook",
          provider: "facebook",
        },
        password: {
          text: t("auth:login"),
          provider: "email",
        },
      };
      const { text, icon, provider } =
        providerMap[loginFormik.values.providerId] || {};
      disabled = !loginFormik.isValid;
      [disabledNotice] = Object.values(loginFormik.errors || {});
      activeText = text || t("auth:register");
      onClickPrev = () => {
        setSurveyLoginFormShown?.(false);
      };
      onClickNext = async () => {
        setSubmitting(true);
        try {
          const user = await (provider
            ? signIn({
                provider,
                formikStore: loginFormik,
                project,
              })
            : createUser(loginFormik));

          handleProcessNext(user).then((success: boolean) => {
            if (success) {
              setIndex((e) => e + 1);
              handleScrollUp();
              setFullscreenMap(false);
              setSurveyLoginFormShown?.(false);
            }
          });
        } catch (error) {
          console.error(error);

          /**
           * @todo check if || t("auth:error.wrongPassword") is necessary
           */

          loginFormik.setErrors({
            password:
              typeof error === "string"
                ? t(error)
                : t("auth:error.wrongPassword"),
          });
        } finally {
          setSubmitting(false);
        }
      };
    }
  }
  if (isLastPage) {
    activeText = t("global:submit");
    trailingIcon = "paperplane";
  }

  // useOnKeyDown((e) => {
  //   if (e.code === "Enter" && !disabled) {
  //     e.preventDefault();
  //     onClickNext();
  //   }
  // });

  if (currentPageRole === "outro") return null;

  // if (currentPageRole === "demographics") {
  //   // if (user?.profileCompleted && !disabled) handleNext();
  //   const { providerId } = demographicsFormik.values;
  //   if (providerId?.length) {
  //     disabled = !demographicsFormik.isValid;
  //     [disabledNotice] = Object.values(demographicsFormik.errors);
  //   }
  // }
  return (
    // biome-ignore lint/nursery/useUniqueElementIds: <explanation>
    <div
      className={cn(
        "absolute bottom-0 end-0 flex flex-col ",
        !preview && "fixed",
        isMobile &&
          "border-border-subdued z-50 flex w-full justify-end border-t bg-white",
      )}
      id="survey-footer-container"
    >
      <NavigationButtons
        prev={{
          disabled: disabledPrev,
          disabledNotice: disabledPrevNotice,
          show:
            ((!isMobile || !featureId) &&
              currentPageRole !== "intro" &&
              pages.length > 1) ||
            !!surveyLoginFormShown,
          onClick: onClickPrev,
          ariaLabel: t("global:previous"),
        }}
        next={{
          show: (!isMobile || !featureId) && pages.length > 0,
          onClick: onClickNext,
          disabled,
          disabledNotice,
          loading: submitting,
          trailingIcon,
          text: phase === "while" ? activeText : inactiveText,
          ariaLabel: t("global:next"),
        }}
        extraButton={
          currentPageRole === "intro" &&
          (project?.settings?.languages?.length ||
            project?.settings?.accessibility) ? (
            <div className="flex items-center gap-2">
              {project?.settings?.languages?.length ? (
                <LanguageButton project={project} size="lg" />
              ) : null}
              {project?.settings?.accessibility ? (
                <AccessibilitySettings
                  project={project}
                  size="lg"
                  variant="basic"
                />
              ) : null}
            </div>
          ) : null
        }
      />
    </div>
  );
};
const ViewSurvey: FC<ViewSurveyProps> = (props) => {
  const { t } = useTranslation();
  const { index, survey, surveyMode } = props;
  const isMobileFallback = useIsMobile();
  const isMobile = props.isMobile ?? isMobileFallback;
  const { pages } = survey || {};
  const [openDescription, setOpenDescription] = useState(false);
  const [featureId, setFeatureId] = useState<string | undefined>();
  const [fullscreenMap, setFullscreenMap] = useState(false);

  const { currentPage, currentPageRole, progress } = usePrepareSurveyPage({
    pages,
    index,
  });
  const mainRef = useRef<HTMLDivElement>(null);

  const showCloseSurveyButton = !!(
    !props.preview &&
    props.handleClose &&
    !fullscreenMap &&
    props.project?.settings?.publicAccess !== false &&
    !!props.project?.title
  );

  if (!pages) return null;

  return (
    <main
      ref={mainRef}
      tabIndex={-1}
      className={cn(
        "relative h-[var(--h-screen,100vh)] w-[var(--w-screen,100vw)]",
        props.preview &&
          "preview duration-500 ease-expressive-out transition-[width]",
        props.className,
      )}
      style={
        {
          "--h-progress-bar": "6px",
        } as React.CSSProperties
      }
    >
      {/** biome-ignore lint/nursery/useUniqueElementIds: <explanation> */}
      <form
        className="h-full w-full grid grid-rows-[auto_1fr]"
        id="viewSurvey"
        aria-label={`${t("global:survey")}: ${survey.title}`}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <ProgressBar
          value={progress}
          aria-label={t("survey:step", {
            step: index + 1,
            total: pages.length,
          })}
          round={false}
          className="z-50 w-full"
        />

        <SurveyWrapper
          {...props}
          pages={pages}
          activePage={pages[index]?.uid}
          mode="display"
          isMobile={isMobile}
          openDescription={openDescription}
          setOpenDescription={setOpenDescription}
          featureId={featureId}
          setFeatureId={setFeatureId}
          fullscreenMap={fullscreenMap}
          setFullscreenMap={setFullscreenMap}
          showCloseSurveyButton={showCloseSurveyButton}
        />

        <SurveyFooter
          {...props}
          isMobile={isMobile}
          currentPage={currentPage}
          currentPageRole={currentPageRole}
          featureId={featureId}
          setFeatureId={setFeatureId}
          openDescription={openDescription}
          setOpenDescription={setOpenDescription}
          setFullscreenMap={setFullscreenMap}
          project={props?.project}
          mainRef={mainRef}
        />
        {showCloseSurveyButton && (
          <CloseSurvey
            {...props}
            fullscreenMap={fullscreenMap}
            preview={props.preview}
          />
        )}
      </form>
      <Toast
        leadingIcon="alert-circle-fill"
        variant="critical"
        show={
          (surveyMode === "test" || surveyMode === "preview") &&
          currentPageRole === "intro"
        }
        className="left-0 right-auto"
      >
        {t("survey:testLinkDescription")}
      </Toast>
    </main>
  );
};

export default ViewSurvey;
